import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { MarketCalendar, recordDateEventsForYear, type YutaiMaster } from "@yct/core";
import { api, type CrossPlan, type Holding, type YutaiItem } from "../lib/api.js";
import { categoryLabel, data, recordDayLabel, restrictionLabel, type RestrictionEntry, type StockRow } from "../lib/data.js";
import { recordView } from "../lib/view-history.js";
import { useSession } from "../lib/auth.js";
import { ErrorText, Field, marginTypeLabel, stateLabel, statusLabel, yen } from "../components/ui.js";

const cal = new MarketCalendar();

export function StockDetailPage() {
  const { code = "" } = useParams();
  const { data: session } = useSession();
  const [stock, setStock] = useState<StockRow | null>(null);
  const [master, setMaster] = useState<YutaiMaster | null>(null);
  const [price, setPrice] = useState<{ date: string; close: number } | null>(null);
  const [restrictions, setRestrictions] = useState<RestrictionEntry[]>([]);
  const [gyakuhibu, setGyakuhibu] = useState<Array<{ date: string; ratePerShare: number; days: number }>>([]);
  const [tab, setTab] = useState<"yutai" | "gyakuhibu" | "restrictions" | "mine">("yutai");
  const [favorite, setFavorite] = useState<boolean | null>(null);
  const [mine, setMine] = useState<{ holdings: Holding[]; plans: CrossPlan[]; items: YutaiItem[] } | null>(null);

  useEffect(() => {
    if (!code) return;
    recordView(code);
    data.stocks().then((s) => setStock(s.find((x) => x.code === code) ?? null));
    data.yutai(code).then(setMaster);
    data.prices().then((p) => setPrice(p.prices[code] ?? null));
    data.restrictions().then((r) => setRestrictions(r[code] ?? []));
    data.gyakuhibu(code).then((h) => setGyakuhibu([...h].reverse()));
  }, [code]);

  useEffect(() => {
    if (!session || !code) return;
    api.favorites().then((r) => setFavorite(r.favorites.some((f) => f.stockCode === code))).catch(() => setFavorite(null));
    Promise.all([api.holdings(), api.crossPlans(), api.yutaiItems(true)])
      .then(([h, p, y]) =>
        setMine({
          holdings: h.holdings.filter((x) => x.stockCode === code),
          plans: p.plans.filter((x) => x.stockCode === code),
          items: y.items.filter((x) => x.stockCode === code),
        }),
      )
      .catch(() => setMine(null));
  }, [session, code]);

  const toggleFavorite = async () => {
    if (favorite === null) return;
    if (favorite) await api.removeFavorite(code);
    else await api.addFavorite(code);
    setFavorite(!favorite);
  };

  const year = new Date().getFullYear();
  const events = master
    ? [year, year + 1].flatMap((y) => recordDateEventsForYear(cal, y, master.recordMonths, master.recordDayType, master.recordDay))
    : [];
  const today = new Date().toISOString().slice(0, 10);
  const next = events.find((e) => e.lastTradingDay >= today);
  const stopLevel = restrictions.some((r) => r.level === "stop") ? "stop" : restrictions.length > 0 ? "caution" : null;
  const name = stock?.name ?? mine?.holdings[0]?.stockName ?? "";

  return (
    <section>
      <div className="row between">
        <h1>
          {code} {name}
        </h1>
        {session && favorite !== null && (
          <button className={`btn ${favorite ? "primary" : ""}`} type="button" onClick={toggleFavorite}>
            {favorite ? "★ お気に入り" : "☆ お気に入り"}
          </button>
        )}
      </div>
      <p className="muted">
        {stock ? `${stock.market} / ${stock.sector}` : "銘柄マスタ未登録"}
        {stock?.isLoanable ? " / 貸借銘柄" : ""}
        {stopLevel && <span className={`badge ${stopLevel}`}> {stopLevel === "stop" ? "停止" : "注意"}</span>}
      </p>
      <div className="card">
        <dl className="dl">
          <dt>株価</dt>
          <dd>{price ? `${price.close.toLocaleString("ja-JP")}円（${price.date} 終値）` : "未取得"}</dd>
          {next && (
            <>
              <dt>次回 権利付最終日</dt>
              <dd>{next.lastTradingDay}</dd>
              <dt>権利落ち日</dt>
              <dd>{next.exDate}</dd>
              <dt>権利確定日</dt>
              <dd>{next.recordDate}</dd>
            </>
          )}
        </dl>
        <div className="row wrap">
          <Link className="btn" to="/my/holdings">
            所持登録
          </Link>
          <Link className="btn primary" to={`/cross/new?code=${code}&name=${encodeURIComponent(name)}${next ? `&lastTradingDay=${next.lastTradingDay}` : ""}${price ? `&price=${price.close}` : ""}`}>
            クロス予定に追加
          </Link>
        </div>
      </div>

      <div className="row wrap">
        {(["yutai", "gyakuhibu", "restrictions", "mine"] as const).map((t) => (
          <button key={t} type="button" className={`btn small ${tab === t ? "primary" : ""}`} onClick={() => setTab(t)}>
            {{ yutai: "優待", gyakuhibu: "逆日歩", restrictions: "制限", mine: "自分の履歴" }[t]}
          </button>
        ))}
      </div>

      {tab === "yutai" && (
        <div className="card">
          {master ? (
            <>
              <p>
                権利確定月: {master.recordMonths.join("・")}月（{recordDayLabel[master.recordDayType] ?? ""}） / 種別: {master.categoryTags.map((t) => categoryLabel[t] ?? t).join("・")}
              </p>
              <ul className="list">
                {master.tiers.map((t) => (
                  <li key={t.minShares}>
                    <strong>{t.minShares.toLocaleString("ja-JP")}株〜</strong> {t.description}
                    {t.valueYen != null && <span className="muted">（目安 {yen(t.valueYen)}）</span>}
                    {t.longTermDescription && <div className="muted">長期: {t.longTermDescription}</div>}
                  </li>
                ))}
              </ul>
              {master.longTermRule && (
                <p className="muted">
                  長期保有条件: {master.longTermRule.type === "holder_number_only" ? "株主番号の継続" : "各基準日に基準株数以上"}を {master.longTermRule.requiredCount} 回連続（判定月 {master.longTermRule.checkMonths.join("・")}月）
                </p>
              )}
              <p className="muted">
                使用期限: {expiryText(master)} / 到着目安: 権利確定日から約 {master.arrivalOffsetMonths} か月
              </p>
              {master.sourceUrl && (
                <a href={master.sourceUrl} target="_blank" rel="noopener noreferrer">
                  情報源
                </a>
              )}
            </>
          ) : (
            <p className="muted">優待情報は未登録です。</p>
          )}
          {session && <ReportForm code={code} />}
        </div>
      )}

      {tab === "gyakuhibu" && (
        <div className="card">
          {gyakuhibu.length === 0 ? (
            <p className="muted">逆日歩の履歴はありません。</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>日付</th>
                  <th>1株あたり</th>
                  <th>日数</th>
                </tr>
              </thead>
              <tbody>
                {gyakuhibu.slice(0, 30).map((g) => (
                  <tr key={g.date}>
                    <td>{g.date}</td>
                    <td>{g.ratePerShare}円</td>
                    <td>{g.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "restrictions" && (
        <div className="card">
          {restrictions.length === 0 ? (
            <p className="muted">現在の制限はありません。</p>
          ) : (
            <ul className="list">
              {restrictions.map((r) => (
                <li key={`${r.kind}-${r.from}`}>
                  <span className={`badge ${r.level}`}>{r.level === "stop" ? "停止" : "注意"}</span> {restrictionLabel[r.kind] ?? r.kind} {r.from}〜{r.to ?? ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "mine" && (
        <div className="card">
          {!session ? (
            <p className="muted">ログインすると所持・クロス・優待の履歴が出ます。</p>
          ) : !mine ? (
            <p className="muted">読み込み中…</p>
          ) : (
            <>
              <h2>所持</h2>
              {mine.holdings.length === 0 ? <p className="muted">なし</p> : <p>{mine.holdings.reduce((s, h) => s + h.shares, 0).toLocaleString("ja-JP")}株（{mine.holdings.length}ロット）</p>}
              <h2>クロス</h2>
              <ul className="list">
                {mine.plans.map((p) => (
                  <li key={p.id}>
                    <Link to={`/cross/${p.id}`}>
                      {p.lastTradingDay} <span className={`badge ${p.status}`}>{statusLabel[p.status]}</span> {p.shares}株 {marginTypeLabel[p.marginType]} {yen(p.estTotal)}
                    </Link>
                  </li>
                ))}
              </ul>
              <h2>優待</h2>
              <ul className="list">
                {mine.items.map((i) => (
                  <li key={i.id}>
                    {i.recordDate} <span className={`badge ${i.state}`}>{stateLabel[i.state]}</span> {i.description}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function expiryText(m: YutaiMaster): string {
  const r = m.expiryRule;
  switch (r.type) {
    case "months_after_record":
      return `権利確定日から ${r.months} か月`;
    case "months_after_arrival":
      return `到着から ${r.months} か月`;
    case "fixed_month_day":
      return `毎年 ${r.month}月${r.day}日まで`;
    case "none":
      return "期限なし";
  }
}

function ReportForm({ code }: { code: string }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("wrong_content");
  const [message, setMessage] = useState("");
  const [url, setUrl] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await fetch(`${import.meta.env.VITE_API_ORIGIN ?? ""}/api/reports`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ stockCode: code, kind, message, ...(url ? { sourceUrl: url } : {}) }),
      }).then((r) => {
        if (!r.ok) throw new Error(`送信に失敗しました (${r.status})`);
      });
      setDone(true);
      setOpen(false);
    } catch (err) {
      setError(err);
    }
  };

  if (done) return <p className="muted">報告ありがとうございます。確認のうえ反映します。</p>;
  if (!open) {
    return (
      <button className="btn link" type="button" onClick={() => setOpen(true)}>
        優待情報の間違い・新設を報告
      </button>
    );
  }
  return (
    <form className="form" onSubmit={submit}>
      <Field label="種類">
        <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="wrong_content">内容が違う</option>
          <option value="expiry">使用期限の情報</option>
          <option value="new_yutai">優待が新設された</option>
          <option value="discontinued">優待が廃止された</option>
          <option value="other">その他</option>
        </select>
      </Field>
      <Field label="内容">
        <textarea className="input" required maxLength={1000} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </Field>
      <Field label="情報源 URL（任意）">
        <input className="input" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <ErrorText error={error} />
      <div className="row">
        <button className="btn primary" type="submit">
          送信
        </button>
        <button className="btn" type="button" onClick={() => setOpen(false)}>
          キャンセル
        </button>
      </div>
    </form>
  );
}
